// src/pages/finance-portal/reports/FinancialRatios.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Stack, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatNumber,
    firstOfYearIso, todayIso,
} from './reportShared';

const CATEGORY_LABELS = {
    liquidity: 'Liquidity',
    profitability: 'Profitability',
    efficiency: 'Efficiency',
    leverage: 'Leverage',
};

export default function FinancialRatios() {
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [industry, setIndustry] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/analytics/ratios', {
                params: {
                    start_date: start, end_date: end,
                    industry_code: industry || undefined,
                },
            });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load ratios');
        } finally { setLoading(false); }
    }, [start, end, industry]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <CalculateIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Financial Ratios</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField label="Start" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField label="End" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField label="Industry Code (optional)" fullWidth size="small"
                            value={industry} onChange={(e) => setIndustry(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && Object.entries(data.categories || {}).map(([cat, rows]) => (
                <Accordion key={cat} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>{CATEGORY_LABELS[cat] || cat}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Metric</TableCell>
                                        <TableCell align="right">Your Value</TableCell>
                                        {industry && <TableCell align="right">Industry Avg</TableCell>}
                                        {industry && <TableCell align="right">P25</TableCell>}
                                        {industry && <TableCell align="right">P50</TableCell>}
                                        {industry && <TableCell align="right">P75</TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((r) => (
                                        <TableRow key={r.key}>
                                            <TableCell>{r.key}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {formatNumber(r.value, 4)}
                                            </TableCell>
                                            {industry && <TableCell align="right">{r.benchmark_avg ?? '—'}</TableCell>}
                                            {industry && <TableCell align="right">{r.benchmark_p25 ?? '—'}</TableCell>}
                                            {industry && <TableCell align="right">{r.benchmark_p50 ?? '—'}</TableCell>}
                                            {industry && <TableCell align="right">{r.benchmark_p75 ?? '—'}</TableCell>}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
}
